// --------------------------------------------------------------
// Created On: 2021-11-18
// Author: Zachary Thomas
//
// Last Modified: 2024-02-27
// Modified By: Zachary Thomas
//
// Copyright 2022 - 2024 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import Card from "../Card/Card";
import Error from "../Error/Error";
import Success from "../Success/Success";
import PrivacyPolicy from "../PrivacyPolicy/PrivacyPolicy";
import useAutoAnimate from "../../hooks/useAutoAnimate";
import PropTypes, { InferProps } from "prop-types";
import PhoneInput from "react-phone-input-2";
import styles from "./ReviewContent.module.scss";

// A card for reviewing content.
export default function ReviewContent(props: Props): Component {
  const [animationParent] = useAutoAnimate<HTMLDivElement>();

  return (
    <div className={`${styles.container} mx-auto`}>
      <Card title={props.title}>
        <div className="mx-4 py-3" ref={animationParent}>
          {props.successMessage.length === 0 && (
            <Fragment>
              <div className="mb-4">
                {/* Input for entering the current persons name. */}
                {props.requireName && (
                  <div className="form-group mb-3">
                    <label className="my-2">Name</label>
                    <input
                      className="form-control"
                      value={props.name}
                      onChange={(e) => props.onChangeName(e.target.value)}
                    />
                  </div>
                )}

                {/* Phone number input. */}
                {props.phoneNumber !== undefined && props.callingCode !== undefined && props.callingCode !== null && (
                  <div className="form-group my-3">
                    <label>Phone Number (optional)</label>
                    <PhoneInput
                      placeholder="Enter phone number..."
                      value={props.phoneNumber}
                      country="us"
                      onChange={(phoneNumber, country) => props.onChangePhoneNumber(phoneNumber, country)}
                    />
                  </div>
                )}

                {/* Forms that show some pre-filled data. */}
                {props.completedForms.map((completedForm, i) => (
                  <div className="form-group my-3" key={i}>
                    <label>{completedForm.key}</label>
                    <input value={completedForm.value} className="form-control" disabled />
                  </div>
                ))}

                {/* Privacy policy agreement. */}
                {props.requirePrivacyPolicy && (
                  <PrivacyPolicy
                    status={props.privacyPolicy}
                    onChange={(status) => props.onChangePrivacyPolicy(status)}
                  />
                )}
              </div>

              {/* Main content body. */}
              <p className={`${styles.body} mt-3 mb-5`}>{props.content}</p>
            </Fragment>
          )}

          <div>
            <Success message={props.successMessage} />
            <Error message={props.errorMessage} />
          </div>

          {/* Buttons for accepting or rejecting a request. */}
          {props.successMessage.length === 0 && (
            <div className="row mt-5 mb-3">
              {props.allowReject && (
                <div className="col text-center">
                  <button
                    type="button"
                    className={`${styles.reviewBtn} btn btn-danger mx-auto`}
                    onClick={() => props.onReject()}
                  >
                    Reject
                  </button>
                </div>
              )}

              <div className="col text-center">
                <button
                  type="button"
                  className={`${styles.reviewBtn} btn btn-success mx-auto`}
                  onClick={() => props.onAccept()}
                >
                  Accept
                </button>
              </div>
            </div>
          )}

          {props.children}
        </div>
      </Card>
    </div>
  );
}

ReviewContent.propTypes = {
  title: PropTypes.string.isRequired,
  completedForms: PropTypes.array.isRequired,
  content: PropTypes.string.isRequired,
  successMessage: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  allowReject: PropTypes.bool.isRequired,
  requireName: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onChangeName: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string,
  callingCode: PropTypes.string,
  onChangePhoneNumber: PropTypes.func.isRequired,
  requirePrivacyPolicy: PropTypes.bool.isRequired,
  privacyPolicy: PropTypes.bool.isRequired,
  onChangePrivacyPolicy: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onAccept: PropTypes.func.isRequired,
  children: PropTypes.any,
};

type Props = InferProps<typeof ReviewContent.propTypes>;
